import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, getDoc } from 'firebase/firestore';
import { getDb } from '../services/firebase';
import { saveBlogToJson, deleteBlogFromJson } from '../services/staticDataService';

export const BLOG_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived'
};

export const BLOG_CATEGORIES = [
  'Recruitment',
  'HR Management',
  'Talent Acquisition',
  'Employee Development',
  'Career Development',
  'Leadership',
  'Workplace Culture',
  'Employee Engagement',
  'Performance Management',
  'HR Technology',
  'Remote Work',
  'Diversity & Inclusion',
  'Compensation & Benefits',
  'Employer Branding',
  'HR Analytics',
  'Talent Management',
  'Onboarding',
  'Training & Development',
  'Workplace Wellness',
  'HR Strategy'
];

const BlogContext = createContext();

export const useBlog = () => useContext(BlogContext);

export const BlogProvider = ({ children }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Blog verilerini getir
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const db = await getDb();
      
      const blogsRef = collection(db, 'blogs');
      const q = query(blogsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const blogsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBlogs(blogsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Blog verileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Blog ekle
  const addBlog = async (blogData) => {
    try {
      const db = await getDb();
      
      const blogsRef = collection(db, 'blogs');
      const docRef = await addDoc(blogsRef, {
        ...blogData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      const newBlog = {
        id: docRef.id,
        ...blogData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setBlogs(prevBlogs => [newBlog, ...prevBlogs]);
      
      // Blog verilerini JSON dosyasına kaydet
      await saveBlogToJson(newBlog);
      
      return docRef.id;
    } catch (err) {
      console.error('Error adding blog:', err);
      throw err;
    }
  };

  // Blog güncelle
  const updateBlog = async (id, blogData) => {
    try {
      const db = await getDb();
      
      const blogRef = doc(db, 'blogs', id);
      await updateDoc(blogRef, {
        ...blogData,
        updatedAt: new Date()
      });
      
      setBlogs(prevBlogs => 
        prevBlogs.map(blog => 
          blog.id === id 
            ? { ...blog, ...blogData, updatedAt: new Date() }
            : blog
        )
      );
      
      // Blog verilerini JSON dosyasında güncelle
      await saveBlogToJson({ id, ...blogData, updatedAt: new Date() });
      
      return true;
    } catch (err) {
      console.error('Error updating blog:', err);
      throw err;
    }
  };

  // Blog sil
  const deleteBlog = async (id) => {
    try {
      const db = await getDb();
      
      const blogRef = doc(db, 'blogs', id);
      await deleteDoc(blogRef);
      
      setBlogs(prevBlogs => prevBlogs.filter(blog => blog.id !== id));
      
      // Blog verilerini JSON dosyasından sil
      await deleteBlogFromJson(id);
      
      return true;
    } catch (err) {
      console.error('Error deleting blog:', err);
      throw err;
    }
  };

  // Blog detaylarını getir
  const getBlog = async (id) => {
    try {
      const db = await getDb();
      
      const blogRef = doc(db, 'blogs', id);
      const blogSnap = await getDoc(blogRef);
      
      if (blogSnap.exists()) {
        return {
          id: blogSnap.id,
          ...blogSnap.data()
        };
      }
      
      return null;
    } catch (err) {
      console.error('Error getting blog:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const value = {
    blogs,
    loading,
    error,
    addBlog,
    updateBlog,
    deleteBlog,
    getBlog,
    fetchBlogs
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
};

export default BlogContext;
